// Analytics tracking utilities
import { logEvent } from 'firebase/analytics';
import { analytics } from '../config/firebase';

/**
 * Track a custom event in Google Analytics
 */
export const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
  // Gracefully handle when analytics is not available (ad blockers, privacy settings, etc.)
  if (!analytics) {
    // Silently skip - don't warn in production to avoid console noise
    if (process.env.NODE_ENV === 'development') {
      console.warn('Analytics not initialized, skipping event:', eventName);
    }
    return;
  }

  try {
    logEvent(analytics, eventName, eventParams);
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Event tracked:', eventName, eventParams);
    }
  } catch (error) {
    // Silently fail - analytics errors should never break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to track event:', eventName, error);
    }
  }
};

/**
 * Track page views
 */
export const trackPageView = (pageName: string, pageTitle?: string) => {
  trackEvent('page_view', {
    page_title: pageTitle || pageName,
    page_location: window.location.href,
    page_path: window.location.pathname,
  });
};

/**
 * Track App Store button clicks
 */
export const trackAppStoreClick = (platform: 'ios' | 'android') => {
  trackEvent('app_store_click', {
    platform,
    source: 'landing_page',
  });
};

/**
 * Track contact form interactions
 */
export const trackContactFormSubmit = (formType: string) => {
  trackEvent('contact_form_submit', {
    form_type: formType,
  });
};

/**
 * Track language changes
 */
export const trackLanguageChange = (language: string) => {
  trackEvent('language_change', {
    language,
    previous_language: localStorage.getItem('language') || 'de',
  });
};

/**
 * Track navigation to legal pages
 */
export const trackLegalPageView = (pageType: 'privacy' | 'terms' | 'imprint' | 'eula') => {
  trackEvent('legal_page_view', {
    page_type: pageType,
  });
};

/**
 * Track feature interactions
 */
export const trackFeatureClick = (featureName: string) => {
  trackEvent('feature_click', {
    feature_name: featureName,
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (percentage: number) => {
  trackEvent('scroll_depth', {
    percentage,
  });
};
